import React, { useEffect, useState, useMemo } from 'react';
import { isPlaceholderCustomerCode } from '../../utils/excelAccountImporter';
import {
  X,
  User,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  Calendar,
  AlertTriangle,
  FileText,
  DollarSign,
  Layers,
  MessageSquare,
  Plus,
  Send,
  ExternalLink,
  Sparkles,
  PhoneCall,
  CheckCircle2,
  Clock,
  Navigation,
  Share2,
  ShieldCheck,
  ChevronRight,
  Receipt,
  Eye,
  FileSpreadsheet,
  Download,
  Camera,
  Mic,
  HardDrive,
  Search,
  Trash2,
  Calculator,
  Archive,
  Star,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { isSystemNote, MAX_USER_NOTES_PER_ACCOUNT, productOf, originalOutstandingOf } from '../../utils/collections';
import { isOTSExpired, getBankOTSValidTill } from '../../utils/otsScheme';
import { getLinkedAccounts } from '../../utils/linkedAccounts';
import { Account, LinkedAccountSummary } from '../../types';
import { formatINR } from '../../utils/watermark';
import {
  LogFollowUpModal,
  CreatePTPModal,
  RecordRecoveryModal,
  UploadDocumentModal,
  AIRecoveryDrawer,
  AddAgentNoteModal,
} from './ActionModals';
import { OTSSchemeModal } from './OTSSchemeModal';
import { WhatsAppOTSOfferModal } from './WhatsAppOTSOfferModal';
import { WhatsAppDraftModal, WhatsAppCategory } from './WhatsAppDraftModal';
import { StarRating } from './StarRating';
import { CloseAccountModal } from './CloseAccountModal';

import { displayDate } from '../../utils/dates';
interface CustomerConsolidatedDetailModalProps {
  account: Account | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectLinkedAccount?: (accountId: string) => void;
  onOpenNoteModal?: () => void;
  onOpenRecoveryModal?: () => void;
  onOpenPTPModal?: () => void;
}

export const CustomerConsolidatedDetailModal: React.FC<CustomerConsolidatedDetailModalProps> = ({
  account: accountProp,
  isOpen,
  onClose,
  onSelectLinkedAccount,
  onOpenNoteModal,
  onOpenRecoveryModal,
  onOpenPTPModal,
}) => {
  const {
    accounts,
    updateAccount,
    updateCustomerStarRating,
    closeAccount,
    deleteAccount,
    markAccountForDeletion,
    addAgentNote,
    deleteAgentNote,
    deleteGeoTaggedPhoto,
    updatePhotoNote,
    currentUser,
    startFieldVisit,
    documents,
    photos,
    voiceNotes,
    recoveries,
    branches,
    areas,
    zones,
    correctAccountOriginalBalance,
  } = useSRMS();

  /**
   * THE ACCOUNT AS IT IS NOW. Same reason as in the OTS window: this screen
   * was handed a COPY taken when it opened, so a settlement applied or
   * cancelled from inside it left the banner and the buttons showing the old
   * status until the page was refreshed. It now reads the live record.
   */
  const account = useMemo(() => {
    if (!accountProp) return accountProp;
    const live = accounts.find(
      (a) => a.accountId === accountProp.accountId || a.id === accountProp.id
    );
    return live || accountProp;
  }, [accounts, accountProp]);

  const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);
  const [isMarkingForDeletion, setIsMarkingForDeletion] = useState(false);

  const handleDeleteThisCustomer = async () => {
    if (!account) return;
    const confirmMsg = `Are you sure you want to permanently delete customer "${account.customerName}" (Account ID: ${account.accountId})?\n\nThis action cannot be undone and can be performed by Admin only.`;
    if (!window.confirm(confirmMsg)) return;

    setIsDeletingCustomer(true);
    try {
      // Awaited, and the result is read. Previously the delete was fired and
      // the window closed straight away, discarding the answer -- so a
      // refusal was invisible and the account reappeared on the next sync.
      const res = await deleteAccount(account.accountId);
      if (!res?.success) {
        alert(res?.message || 'Could not delete this customer.');
        setIsDeletingCustomer(false);
        return;
      }
      onClose();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete customer.');
      setIsDeletingCustomer(false);
    }
  };

  const handleMarkCustomerForDeletion = () => {
    if (!account) return;
    const confirmMsg = `Mark customer "${account.customerName}" (${account.accountId}) for permanent deletion?\n\nThis will send a deletion request to Admin for final review and removal.`;
    if (window.confirm(confirmMsg)) {
      setIsMarkingForDeletion(true);
      markAccountForDeletion(account.accountId);
      setIsMarkingForDeletion(false);
      setSaveSuccessMsg('Customer marked for permanent deletion. Admin notified.');
      setTimeout(() => setSaveSuccessMsg(''), 4000);
    }
  };

  // If the account this window is showing has been deleted, close rather
  // than carry on rendering a record that no longer exists.
  useEffect(() => {
    if (!isOpen || !account) return;
    const stillThere = accounts.some(
      (a) => a.accountId === account.accountId || a.id === account.id
    );
    // Deferred for the same reason as above: closing during the update that
    // removed the account piles two teardowns into one render pass.
    if (!stillThere) {
      const t = window.setTimeout(() => onClose(), 0);
      return () => window.clearTimeout(t);
    }
  }, [accounts, account, isOpen, onClose]);

  // Local state for interactive note entry
  const [newNoteText, setNewNoteText] = useState('');
  const [noteFeedback, setNoteFeedback] = useState('');

  /** Whether this bank's settlement window is still open. */
  const otsSchemeWindow = useMemo(
    () => isOTSExpired((account as any)?.bankName || (account as any)?.bank),
    [(account as any)?.bankName, (account as any)?.bank, account?.accountId]
  );
  const [newNoteCategory, setNewNoteCategory] = useState('General');
  const [activeTab, setActiveTab] = useState<
    'overview' | 'uploaded_data' | 'accounts' | 'documents' | 'notes' | 'actions'
  >('overview');
  const [uploadedFieldFilter, setUploadedFieldFilter] = useState('');

  // Editing Customer Location & Details
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    customerName: account?.customerName || '',
    mobile: account?.mobile || '',
    alternateMobile: account?.alternateMobile || '',
    city: account?.city || 'N/A',
    area: account?.area || 'N/A',
    zone: account?.zone || 'N/A',
    coordinates: account?.coordinates || '',
    branch: account?.branch || 'Main Branch',
    address: account?.address || '',
    npaDate: account?.npaDate || '',
    riskClassification: account?.riskClassification || 'Medium Risk',
    // Deliberately blank. See the note on the sync effect below.
    latestRemark: '',
  });
  const [saveSuccessMsg, setSaveSuccessMsg] = useState('');

  // Sub-action modals
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showPTP, setShowPTP] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showOTSScheme, setShowOTSScheme] = useState(false);
  const [showWhatsAppOTSOffer, setShowWhatsAppOTSOffer] = useState(false);
  const [showWhatsAppDraft, setShowWhatsAppDraft] = useState(false);
  const [whatsAppCategory, setWhatsAppCategory] = useState<WhatsAppCategory>('PAYMENT_REMINDER');

  // Sync edit form on account change
  React.useEffect(() => {
    if (account) {
      setEditForm({
        customerName: account.customerName || '',
        mobile: account.mobile || '',
        alternateMobile: account.alternateMobile || '',
        city: account.city || 'N/A',
        area: account.area || 'N/A',
        zone: account.zone || 'N/A',
        coordinates: account.coordinates || '',
        branch: account.branch || 'Main Branch',
        address: account.address || '',
        npaDate: account.npaDate || '',
        riskClassification: account.riskClassification || 'Medium Risk',
        // The remark box opens empty on purpose. It is a place to record what
        // happened on THIS visit, and pre-filling it with the last visit's
        // remark meant agents kept saving a stale line back unchanged, or had
        // to clear somebody else's words before they could type. Leaving it
        // blank asks for a fresh remark; the previous one is untouched on the
        // record and is shown in the read-only details below.
        latestRemark: '',
      });
      setIsEditingDetails(false);
      setSaveSuccessMsg('');
    }
  }, [account]);

  /**
   * THE LAST TWO HOOKS, MOVED UP HERE -- THIS IS THE WHOLE FIX FOR THE CRASH
   * WHEN THE CUSTOMER WINDOW IS CLOSED IN THE MOBILE APP.
   *
   * These two were declared further down, BELOW the `return null` on the next
   * line. React requires every render of a component to call exactly the same
   * hooks in exactly the same order, and counts them as it goes.
   *
   * Open the customer window and this component runs all of its hooks. Close
   * it and `isOpen` turns false, so it returned at the line below -- having
   * run two hooks FEWER than the render before. React sees the count drop,
   * cannot match up what it has stored, and throws: "Rendered fewer hooks than
   * expected", which reaches the person as minified error #300 and the red
   * "Something on this screen stopped working" panel.
   *
   * WHY ONLY THE MOBILE APP. The web portal mounts this window conditionally
   * -- when there is no customer to show, the component is not rendered at
   * all, so the bad render never happens. The mobile app keeps it mounted and
   * flips `isOpen` instead, which is exactly the render that breaks.
   *
   * Moved above the early return they are reached on every render, in the same
   * order, and the count can no longer change. Nothing else about them
   * changes: both are plain `useState(null)` with no dependency on the account
   * or on anything the early return was guarding against.
   */
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  if (!isOpen || !account) return null;

  const handleSaveDetails = (e: React.FormEvent) => {
    e.preventDefault();
    // The remark box starts empty every time it is opened, so an untouched box
    // means "I have nothing new to add" -- not "delete what is on record".
    // Without this the agent correcting a phone number would silently wipe the
    // remark somebody left after last week's visit.
    const remarkToSave = editForm.latestRemark.trim()
      ? editForm.latestRemark
      : (account.latestRemark || '');
    updateAccount(account.accountId, {
      customerName: editForm.customerName,
      mobile: editForm.mobile,
      alternateMobile: editForm.alternateMobile,
      city: editForm.city,
      area: editForm.area,
      zone: editForm.zone,
      coordinates: editForm.coordinates,
      branch: editForm.branch,
      address: editForm.address,
      npaDate: editForm.npaDate,
      riskClassification: editForm.riskClassification,
      latestRemark: remarkToSave,
    });
    setIsEditingDetails(false);
    // Clear the box again so the next edit starts from a blank remark.
    setEditForm((prev) => ({ ...prev, latestRemark: '' }));
    setSaveSuccessMsg('Customer details updated successfully!');
    setTimeout(() => setSaveSuccessMsg(''), 4000);
  };

  // Find all genuine linked accounts for this customer
  // One matching rule for the whole app, plus any links recorded explicitly
  // on the account. The copy that lived here treated a blank customer code as
  // a match, so unrelated borrowers were shown as one customer.
  const relatedAccounts: Account[] = (() => {
    const out = getLinkedAccounts(account, accounts);
    const seen = new Set(out.map((a) => a.accountId));
    (account.linkedAccountIds || []).forEach((id) => {
      if (seen.has(id)) return;
      const found = accounts.find((a) => a.accountId === id || a.id === id);
      if (found) {
        out.push(found);
        seen.add(found.accountId);
      }
    });
    return out;
  })();

  const isMultiple = relatedAccounts.length > 1 || account.isMultipleAccount;
  const totalOutstanding = relatedAccounts.reduce((sum, a) => sum + (a.outstandingAmount || 0), 0);
  // Whole-customer original, so the concession matches the OTS window (all loans).
  const mergedOriginal = relatedAccounts.reduce(
    (sum, a) => sum + (originalOutstandingOf(a) || 0),
    0
  );
  const totalSanction = relatedAccounts.reduce((sum, a) => sum + (a.sanctionAmount || 0), 0);
  const totalEMI = relatedAccounts.reduce((sum, a) => sum + (a.emi || 0), 0);
  const totalRecovered = relatedAccounts.reduce((sum, a) => sum + (a.totalRecovered || 0), 0);

  // Attached media/documents for all related accounts
  const accountDocs = documents.filter(
    (d) => d.accountId === account.accountId || relatedAccounts.some((r) => r.accountId === d.accountId)
  );
  const accountPhotos = photos.filter(
    (p) => p.accountId === account.accountId || relatedAccounts.some((r) => r.accountId === p.accountId)
  );
  const accountVoices = voiceNotes.filter(
    (v) => v.accountId === account.accountId || relatedAccounts.some((r) => r.accountId === v.accountId)
  );

  // Every receipt raised against this customer, not just the loan account that
  // happens to be open. A borrower with four loans pays once and the agent is
  // asked "what has he paid so far?" about the person, not about one loan, so
  // the related accounts are folded in the same way the documents above are.
  //
  // Sorted newest first: the recording screens write recoveryDate as
  // YYYY-MM-DD, so a plain string compare puts the latest receipt on top.
  const accountRecoveries = recoveries
    .filter(
      (r) =>
        r.accountId === account.accountId ||
        relatedAccounts.some((a) => a.accountId === r.accountId)
    )
    .slice()
    .sort((a, b) => (b.recoveryDate || '').localeCompare(a.recoveryDate || ''));

  const accountRecoveriesTotal = accountRecoveries.reduce((sum, r) => sum + (r.amount || 0), 0);

  // Combine notes from all related accounts
  const allNotes = relatedAccounts.flatMap((a) =>
    (a.agentNotesHistory || []).map((n) => ({
      ...n,
      accountLoanNumber: a.loanNumber,
      accountFacility: a.facility || a.loanType,
    }))
  );
  // Sort descending by date
  allNotes.sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));

  /**
   * An AGENT's note and the app's own bookkeeping are two different things.
   *
   * `agentNotesHistory` holds both, mixed: remarks a person typed, and
   * entries the app writes for itself -- "[Collection Received]: ...",
   * "[OTS Scheme Calculated]: ...", "[Customer Classification]: ..." and
   * about a dozen more.
   *
   * This window showed the newest entry of either kind as "Latest Agent
   * Note". The app writes far more of them than an agent does, so what you
   * usually saw in that box was the app talking to itself, and the agent's
   * actual remark about the customer had been pushed out of sight by the
   * next receipt.
   *
   * So this window now shows only what a person wrote, everywhere: the
   * "Latest Agent Note" box, the Agent Notes tab and the tab count. Nothing
   * is deleted -- the app's entries stay on the account exactly as before and
   * are read in the account timeline, which is the proper place for an audit
   * trail. They are simply no longer mixed into an agent's own notes.
   */
  const agentNotes = allNotes.filter((n) => !isSystemNote(n));

  // Photo deletion from the customer window. Admins may delete any photo;
  // agents only their own -- the context enforces that.
  // (`deletingPhotoId` itself is declared with the other hooks above the early
  // return -- see the note there.)

  const handleDeletePhotoInWindow = async (photo: any) => {
    const id = photo.id || photo.photoId;
    const ok = window.confirm(
      `Delete this geo-tagged photo?\n\nAccount: ${photo.accountId || account.accountId}\n\nThis cannot be undone.`
    );
    if (!ok) return;

    const reason = window.prompt('Reason (optional):') || '';

    setDeletingPhotoId(id);
    try {
      const res = await deleteGeoTaggedPhoto(id, reason);
      if (!res.success) alert(res.message);
    } finally {
      setDeletingPhotoId(null);
    }
  };

  // A note left blank in the field can be filled in once from here. The
  // context enforces the once-only rule and who is allowed to do it.
  // (`editingNoteId` is declared with the other hooks above the early return.)

  const handleEditPhotoNoteInWindow = async (photo: any) => {
    const id = photo.id || photo.photoId;
    const next = window.prompt(
      photo.agentRemark
        ? 'Correct the note on this photo:'
        : 'Add the note for this photo (this can only be done once):',
      photo.agentRemark || ''
    );
    if (next === null) return;

    setEditingNoteId(id);
    try {
      const res = await updatePhotoNote(id, next);
      if (!res.success) alert(res.message);
    } finally {
      setEditingNoteId(null);
    }
  };

  const handleAddInlineNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;

    const res = addAgentNote(account.accountId, newNoteText.trim(), newNoteCategory);
    setNoteFeedback(res.message);
    window.setTimeout(() => setNoteFeedback(''), 6000);
    // A refused note is NOT cleared from the box. Losing what somebody typed
    // because the twentieth note was already there would be its own bug.
    if (res.success) setNewNoteText('');
  };

  /** Delete one note. The context decides whether this person may. */
  const handleDeleteNote = (noteId: string, noteText: string) => {
    if (
      !window.confirm(
        `Delete this note?\n\n"${noteText.slice(0, 160)}${noteText.length > 160 ? '…' : ''}"\n\nThis cannot be undone.`
      )
    )
      return;
    const res = deleteAgentNote(account.accountId, noteId);
    setNoteFeedback(res.message);
    window.setTimeout(() => setNoteFeedback(''), 6000);
  };

  /** True when the signed-in person may remove this particular note. */
  const canDeleteNote = (note: { agentId?: string }) => {
    if (currentUser.role === 'admin') return true;
    const mine = (note.agentId || '').trim().toLowerCase();
    return (
      !!mine &&
      (mine === (currentUser.agentId || '').trim().toLowerCase() ||
        mine === (currentUser.id || '').trim().toLowerCase())
    );
  };

  const handleInitiateVisit = () => {
    startFieldVisit(account.accountId);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-0 sm:p-2 font-sans animate-in fade-in duration-200">
        {/* Phones: use the visible screen height (dvh), so the header and the
            action footer are not hidden under the browser bar. */}
        <div className="bg-white border border-slate-200 sm:rounded-2xl rounded-none w-full max-w-7xl h-[100dvh] max-h-[100dvh] sm:h-[96vh] sm:max-h-[96vh] flex flex-col shadow-2xl text-slate-800 overflow-hidden">
          
          {/* Top Bar / Header */}
          <div className="px-5 py-4 bg-[#0f172a] text-white flex items-start justify-between border-b border-slate-800">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-11 h-11 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-black text-lg shrink-0">
                {account.customerName ? account.customerName.charAt(0).toUpperCase() : 'C'}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
                    {account.customerName}
                  </h2>
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-blue-500/20 text-blue-300 border border-blue-400/30">
                    <Layers className="w-3 h-3" />
                    {relatedAccounts.length === 1 ? '1 Loan Account' : `${relatedAccounts.length} Loan Accounts`}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                    account.accountStatus === 'Active' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                    account.accountStatus === 'PTP' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                    account.accountStatus === 'Settled' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                    'bg-red-500/20 text-red-300 border-red-500/30'
                  }`}>
                    {account.accountStatus}
                  </span>
                  {account.riskClassification && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      Risk: {account.riskClassification}
                    </span>
                  )}
                </div>

                {/* Customer Star Rating Classification Bar */}
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <div className="bg-slate-800/80 border border-slate-700/80 px-2.5 py-1 rounded-xl flex items-center gap-2">
                    <span className="text-[11px] text-slate-300 font-semibold">Classification:</span>
                    <StarRating
                      rating={account.customerStarRating || 0}
                      onChange={(newRating) => updateCustomerStarRating(account.accountId, newRating)}
                      size="sm"
                      showLabel
                      showPrimeBadge
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300 mt-1">
                  {/* The bank's CIF, or plainly nothing. The importer gives a
                      blank CIF the placeholder "CUST-<account number>" so that
                      borrowers without one are never grouped together by
                      accident -- but printed here in the same style as a real
                      CIF it read as though the bank had given us one. */}
                  <span>
                    CIF / Cust ID:{' '}
                    {isPlaceholderCustomerCode(account.customerCode, account.accountId) ? (
                      <strong className="text-slate-400 font-normal italic">Not provided</strong>
                    ) : (
                      <strong className="text-white font-mono">{account.customerCode}</strong>
                    )}
                  </span>
                  <span>Primary Loan: <strong className="text-white font-mono">{account.loanNumber}</strong></span>
                  <span>Branch: <strong className="text-white">{account.branch}</strong></span>
                  {account.coordinates && (
                    <span>Coordinates: <strong className="text-blue-300 font-mono">{account.coordinates}</strong></span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {account.markedForDeletion && (
                <span className="px-2.5 py-1 bg-amber-500/30 text-amber-200 border border-amber-400/40 rounded-lg text-xs font-bold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Marked for Deletion</span>
                </span>
              )}

              {/* Close or Delete Account Button */}
              <button
                type="button"
                onClick={() => setShowCloseAccountModal(true)}
                className="hidden sm:flex px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold items-center gap-1.5 transition cursor-pointer shadow-xs"
                title="Close Account as per OTS or Regular Close, or Delete & Archive"
              >
                <Archive className="w-3.5 h-3.5" />
                <span>Close / Delete Account</span>
              </button>

              <button
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Everything between the header and the footer scrolls together, so the
              customer's figures no longer squeeze the content area on phones. */}
          <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 p-4 bg-slate-50 border-b border-slate-200">
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 text-[11px] font-medium mb-0.5">
                <span>{isMultiple ? 'Total Outstanding' : 'Loan Balance'}</span>
                <DollarSign className="w-3.5 h-3.5 text-red-500" />
              </div>
              <p className="text-base sm:text-lg font-black text-slate-900 font-mono">
                {formatINR(totalOutstanding)}
              </p>
              {isMultiple ? (
                <span className="text-[10px] text-purple-600 font-medium">Across {relatedAccounts.length} loan accounts</span>
              ) : (
                <span className="text-[10px] text-slate-500">1 Loan Account</span>
              )}

              {/*
                CORRECTING A BALANCE THAT IS WRONG.

                The original loan balance is set once, by the first sheet that
                carries the account, and no later upload can move it -- by
                then collections have been recorded against it, and letting a
                spreadsheet silently rewrite the basis of somebody's balance
                would be worse than the problem it solves.

                That left no way to fix a figure that IS wrong. Re-uploading
                the master sheet changes nothing, and there is no field for it
                anywhere. This is the deliberate exception: one account, by an
                administrator, written to the activity log.

                The balance is RECALCULATED from the figure entered and the
                collections that exist -- never set directly -- so it stays
                consistent with the ledger.
              */}
              {(currentUser.role === 'admin' ||
                (currentUser.role as string) === 'super_admin') &&
                !isMultiple && (
                  <button
                    type="button"
                    onClick={async () => {
                      const current = Math.round(
                        Number(account.originalOutstandingAmount) ||
                          Number(account.outstandingAmount) ||
                          0
                      );
                      const typed = window.prompt(
                        'Correct the ORIGINAL loan balance for this account.\n\n' +
                          'This is the figure the bank\u2019s master sheet gives \u2014 what was owed ' +
                          'BEFORE any collection. The balance shown on screen is then worked out ' +
                          'from it and the collections on record.\n\n' +
                          'Enter the correct amount in rupees:',
                        String(current)
                      );
                      if (typed === null) return;
                      const value = Number(String(typed).replace(/[^0-9.]/g, ''));
                      if (!Number.isFinite(value) || value < 0) {
                        window.alert('That is not a valid amount.');
                        return;
                      }
                      const res = await correctAccountOriginalBalance(
                        account.accountId,
                        value
                      );
                      window.alert(res.message);
                    }}
                    className="mt-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer block"
                    title="Set the original loan balance and recalculate from the collections on record"
                  >
                    Correct this balance
                  </button>
                )}
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 text-[11px] font-medium mb-0.5">
                <span>Total Loan Accounts</span>
                <Layers className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <p className="text-base sm:text-lg font-black text-blue-600 font-mono">
                {relatedAccounts.length} {relatedAccounts.length === 1 ? 'Loan' : 'Loans'}
              </p>
              <span className="text-[10px] text-slate-600 font-medium">NPA Date: {displayDate(account.npaDate) || 'N/A'}</span>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 text-[11px] font-medium mb-0.5">
                <span>Total Limit / Sanction</span>
                <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
              </div>
              <p className="text-base sm:text-lg font-black text-slate-800 font-mono">
                {formatINR(totalSanction)}
              </p>
              <span className="text-[10px] text-emerald-600 font-medium">Recovered: {formatINR(totalRecovered)}</span>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 text-[11px] font-medium mb-0.5">
                <span>Assigned Agent</span>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              </div>
              <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                {account.assignedAgentName || 'N/A'}
              </p>
              <span className="text-[10px] text-slate-500 font-mono">{account.assignedAgentId || 'N/A'}</span>
            </div>
          </div>

          {/* Save Success Banner */}
          {saveSuccessMsg && (
            <div className="mx-4 mt-3 p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                {saveSuccessMsg}
              </span>
              <button onClick={() => setSaveSuccessMsg('')} className="text-emerald-700 hover:text-emerald-900">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* OTS Scheme Status Banner */}
          <div
            id="customer-ots-status-banner"
            className="mx-4 mt-3 p-3.5 bg-gradient-to-r from-indigo-50 via-slate-50 to-blue-50 border border-indigo-200 rounded-2xl flex flex-wrap items-center justify-between gap-3 text-xs shadow-xs"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-indigo-950">One-Time Settlement (OTS) Scheme</span>
                  {account.otsStatus === 'OTS Confirmed' ? (
                    <span className="px-2 py-0.5 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-full font-bold text-[10px]">
                      OTS Confirmed &amp; Locked
                    </span>
                  ) : account.otsStatus === 'OTS Cancelled' ? (
                    <span className="px-2 py-0.5 bg-rose-100 border border-rose-300 text-rose-800 rounded-full font-bold text-[10px]">
                      OTS Cancelled
                    </span>
                  ) : account.otsActive ? (
                    <span className="px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 rounded-full font-bold text-[10px]">
                      OTS Applied
                    </span>
                  ) : (
                    /* Another date that was typed into the page rather than
                       read from the settings, so it never changed. */
                    <span
                      className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${
                        otsSchemeWindow.expired
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}
                    >
                      {otsSchemeWindow.expired
                        ? `OTS closed ${otsSchemeWindow.lastDateIso}`
                        : `Valid till ${getBankOTSValidTill(
                            (account as any).bankName || (account as any).bank
                          ) || 'not set'}`}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono">
                  <span>Orig Outstanding: <strong>₹{(mergedOriginal || account.originalOutstandingAmount || totalOutstanding).toLocaleString('en-IN')}</strong></span>
                  {account.finalOTSAmount ? (
                    <>
                      <span className="text-blue-700">Final Settlement: <strong>₹{account.finalOTSAmount.toLocaleString('en-IN')}</strong></span>
                      <span className="text-emerald-700">Actual Concession: <strong>₹{Math.max(0, (mergedOriginal || 0) - account.finalOTSAmount).toLocaleString('en-IN')}</strong></span>
                    </>
                  ) : (
                    <span className="text-slate-500 font-sans">NPA Date: {displayDate(account.npaDate) || 'None'}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                id="header-open-ots-modal-btn"
                type="button"
                onClick={() => setShowOTSScheme(true)}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer"
              >
                <Calculator className="w-3.5 h-3.5" />
                <span>{account.otsActive ? 'View / Modify OTS' : 'Calculate OTS'}</span>
              </button>

              {/* The WhatsApp OTS Offer button was here AND at the foot of
                  this window. Two buttons doing the same thing, one of them
                  directly above the OTS controls it duplicates. The one at the
                  foot is kept. */}

              <button
                type="button"
                onClick={() => setShowCloseAccountModal(true)}
                className="px-3 py-1.5 font-bold rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer bg-slate-800 hover:bg-slate-900 text-white"
                title="Close Account as per OTS or Regular Close"
              >
                <Archive className="w-3.5 h-3.5 text-rose-400" />
                <span>Close / Delete Account</span>
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="sticky top-0 z-10 flex border-b border-slate-200 px-4 bg-white text-xs font-semibold overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <User className="w-4 h-4" />
              <span className="sm:hidden">Details</span><span className="hidden sm:inline">Customer Details</span>
            </button>

            {/* The raw bank Excel sheet is the bank's own record, with columns
                an agent has no business seeing or being judged against in the
                field. Admins still need it to check what was imported, so it
                is hidden from agents rather than removed. */}
            {currentUser.role !== 'agent' && (
              <button
                onClick={() => setActiveTab('uploaded_data')}
                className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                  activeTab === 'uploaded_data'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                <span>Uploaded Master Record</span>
              </button>
            )}

            <button
              onClick={() => setActiveTab('accounts')}
              className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                activeTab === 'accounts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Layers className="w-4 h-4" />
              <span className="sm:hidden">Accounts</span><span className="hidden sm:inline">All Accounts</span> ({relatedAccounts.length})
            </button>

            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                activeTab === 'documents'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="sm:hidden">Docs</span><span className="hidden sm:inline">Documents &amp; Media</span> ({accountDocs.length + accountPhotos.length + accountVoices.length})
            </button>

            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                activeTab === 'notes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="sm:hidden">Notes</span><span className="hidden sm:inline">Agent Notes</span> ({agentNotes.length})
            </button>

            <button
              onClick={() => setActiveTab('actions')}
              className={`py-2.5 px-3 border-b-2 flex items-center gap-1.5 transition whitespace-nowrap cursor-pointer ${
                activeTab === 'actions'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="sm:hidden">Actions</span><span className="hidden sm:inline">Recovery Actions</span>
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div className="p-3 sm:p-6 space-y-4 text-[13px] leading-relaxed">
            
            {/* TAB 1: CUSTOMER DETAILS OVERVIEW */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                
                {/* Location & Territory Header with Edit Toggle */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 bg-blue-50/70 p-3 rounded-xl border border-blue-200">
                  <div className="space-y-1 min-w-0 flex-1">
                    <span className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-blue-700" />
                      Assigned Location &amp; Risk Profile
                    </span>
                    <p className="text-[11px] text-blue-700">
                      City: <strong>{account.city || 'N/A'}</strong> | Area: <strong>{account.area || 'N/A'}</strong> | Zone: <strong>{account.zone || 'N/A'}</strong> | Branch: <strong>{account.branch}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 flex flex-wrap items-center gap-3">
                      <span>
                        Coordinates: <strong className="text-blue-900 font-mono">{account.coordinates || (typeof account.latitude === 'number' && typeof account.longitude === 'number' ? `${account.latitude.toFixed(4)}, ${account.longitude.toFixed(4)}` : 'N/A')}</strong>
                      </span>
                      {account.coordinates && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(account.coordinates)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-bold hover:underline"
                        >
                          <Navigation className="w-3 h-3" />
                          <span>View on Maps</span>
                        </a>
                      )}
                      <span>Risk Category: <strong className="text-amber-700 font-semibold">{account.riskClassification || 'Medium Risk'}</strong></span>
                      <span>Account Category: <strong className="text-blue-700 font-semibold">{account.accountCategory || '—'}</strong></span>
                      {account.latestRemark && (
                        <span>Remark: <strong className="text-slate-800 italic font-medium">"{account.latestRemark}"</strong></span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsEditingDetails(!isEditingDetails)}
                    className="px-3 py-2 sm:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition flex items-center justify-center gap-1 cursor-pointer w-full sm:w-auto shrink-0"
                  >
                    {isEditingDetails ? 'Cancel Edit' : 'Edit Details & Risk Profile'}
                  </button>
                </div>

                {/* Inline Location & Details Editor Form */}
                {isEditingDetails && (
                  <form onSubmit={handleSaveDetails} className="bg-slate-900 text-white p-4 rounded-xl border border-slate-800 space-y-3 animate-in fade-in">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                      <h4 className="text-xs font-bold text-blue-300 uppercase tracking-wider flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-blue-400" />
                        Update Coordinates, City, Area, Risk &amp; Remark
                      </h4>
                      <span className="text-[10px] text-slate-400">Updates live across all views</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          City:
                        </label>
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          placeholder="e.g. Chhatrapati Sambhajinagar, Pune"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          Area:
                        </label>
                        <input
                          type="text"
                          value={editForm.area}
                          onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
                          placeholder="e.g. Samarth Nagar, Mill Corner, CIDCO"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          GPS Coordinates:
                        </label>
                        <input
                          type="text"
                          value={editForm.coordinates}
                          onChange={(e) => setEditForm({ ...editForm, coordinates: e.target.value })}
                          placeholder="e.g. 19.8762, 75.3433"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          Branch Name:
                        </label>
                        <input
                          type="text"
                          value={editForm.branch}
                          onChange={(e) => setEditForm({ ...editForm, branch: e.target.value })}
                          placeholder="e.g. Main Branch, Mill Corner Branch"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          Customer Risk Classification:
                        </label>
                        <select
                          value={editForm.riskClassification}
                          onChange={(e) => setEditForm({ ...editForm, riskClassification: e.target.value })}
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        >
                          <option value="Low Risk">Low Risk</option>
                          <option value="Medium Risk">Medium Risk</option>
                          <option value="High Risk">High Risk</option>
                          <option value="Critical NPA">Critical NPA</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          NPA Date {currentUser.role !== 'admin' && <span className="text-amber-400 text-[10px]">(Locked for Agents)</span>}:
                        </label>
                        <input
                          type="text"
                          value={editForm.npaDate}
                          disabled={currentUser.role !== 'admin'}
                          onChange={(e) => setEditForm({ ...editForm, npaDate: e.target.value })}
                          placeholder="e.g. 31/03/2024"
                          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                            currentUser.role !== 'admin'
                              ? 'bg-slate-800 text-slate-400 border-slate-700 cursor-not-allowed'
                              : 'bg-slate-950 text-white border-slate-700'
                          }`}
                          title={currentUser.role !== 'admin' ? 'NPA Date can only be modified by Bank/Admin' : 'Admin editable NPA Date'}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[11px] text-slate-300 font-semibold">
                            Agent Remark (15 words limit):
                          </label>
                          <span className={`text-[10px] ${
                            editForm.latestRemark.trim().split(/\s+/).filter(Boolean).length > 15
                              ? 'text-rose-400 font-bold'
                              : 'text-slate-400'
                          }`}>
                            {editForm.latestRemark.trim().split(/\s+/).filter(Boolean).length} / 15 words
                          </span>
                        </div>
                        <input
                          type="text"
                          value={editForm.latestRemark}
                          onChange={(e) => {
                            const val = e.target.value;
                            const words = val.trim().split(/\s+/).filter(Boolean);
                            if (words.length <= 15 || val.length < editForm.latestRemark.length) {
                              setEditForm({ ...editForm, latestRemark: val });
                            }
                          }}
                          placeholder="Type this visit's remark (max 15 words)"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        />
                        {account.latestRemark && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            On record now: <span className="italic text-slate-300">"{account.latestRemark}"</span>
                            {' - '}left as it is if you save without typing a new remark.
                          </p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-[11px] text-slate-300 font-semibold mb-1">
                          Registered Full Address:
                        </label>
                        <textarea
                          rows={2}
                          value={editForm.address}
                          onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                      <button
                        type="button"
                        onClick={() => setIsEditingDetails(false)}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Save Location &amp; Details</span>
                      </button>
                    </div>
                  </form>
                )}

                {/* Contact & Address Card */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Contact Information */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <Phone className="w-4 h-4 text-blue-600" />
                      Contact &amp; Communication Channels
                    </h4>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-200">
                        <div>
                          <span className="text-[10px] text-slate-500 block">Primary Mobile</span>
                          <span className="font-bold text-slate-900 font-mono">{account.mobile}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${account.mobile}`}
                            className="p-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition"
                            title="Call customer"
                          >
                            <PhoneCall className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              setWhatsAppCategory('PAYMENT_REMINDER');
                              setShowWhatsAppDraft(true);
                            }}
                            className="p-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition cursor-pointer"
                            title="Open WhatsApp Notification Studio (Payment Reminder / Lok Adalat / OTS Offer)"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {account.alternateMobile && (
                        <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-200">
                          <div>
                            <span className="text-[10px] text-slate-500 block">Alternate / Guarantor Mobile</span>
                            <span className="font-bold text-slate-900 font-mono">{account.alternateMobile}</span>
                          </div>
                          <a
                            href={`tel:${account.alternateMobile}`}
                            className="p-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg transition"
                          >
                            <PhoneCall className="w-4 h-4" />
                          </a>
                        </div>
                      )}

                      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-slate-700 font-bold flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                            <span>Customer Classification (0 - 5 Stars)</span>
                          </span>
                          {account.isPrimeCustomer && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 font-black text-[10px] rounded-full shadow-xs uppercase tracking-wider">
                              ⭐ Prime Customer
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500">
                          Click stars to classify customer. 4 &amp; 5 stars classify customer as Prime for daily priority tracking. Agents can update rating anytime.
                        </p>
                        <div className="pt-1">
                          <StarRating
                            rating={account.customerStarRating || 0}
                            onChange={(newRating) => updateCustomerStarRating(account.accountId, newRating)}
                            size="md"
                            showLabel
                            showPrimeBadge
                          />
                        </div>
                      </div>

                      <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                        <span className="text-[10px] text-slate-500 block">Customer Risk Classification</span>
                        <span className="font-bold text-slate-800">{account.customerCategory || 'NPA Priority'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Address & Geo-location */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-600" />
                      Registered Address &amp; Territory
                    </h4>

                    <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-2 text-xs">
                      <div>
                        <span className="text-[10px] text-slate-500 block">Full Residential / Office Address:</span>
                        <p className="text-slate-800 font-medium leading-relaxed mt-0.5">
                          {account.address}
                        </p>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-[11px]">
                        <div>
                          <span className="text-slate-500 block">City:</span>
                          <span className="font-semibold text-slate-800">{account.city || '\u2014'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Area:</span>
                          <span className="font-semibold text-slate-800">{account.area || '\u2014'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Zone:</span>
                          <span className="font-semibold text-slate-800">{account.zone || '\u2014'}</span>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-[11px]">
                          <span className="text-slate-500 block">Coordinates:</span>
                          <span className="font-mono text-slate-700">
                            {typeof account.latitude === 'number' && typeof account.longitude === 'number'
                              ? `${account.latitude.toFixed(4)}° N, ${account.longitude.toFixed(4)}° E`
                              : (account.coordinates || 'N/A')}
                          </span>
                        </div>
                        {typeof account.latitude === 'number' && typeof account.longitude === 'number' ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${account.latitude},${account.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-bold text-xs transition"
                          >
                            <Navigation className="w-3.5 h-3.5" />
                            <span>Open in Maps</span>
                          </a>
                        ) : account.coordinates ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(account.coordinates)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-bold text-xs transition"
                          >
                            <Navigation className="w-3.5 h-3.5" />
                            <span>Open in Maps</span>
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Multi-Account Snapshot Table */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-600" />
                      Consolidated Account Breakdown ({relatedAccounts.length} Loans)
                    </h4>
                    <span className="text-[11px] text-slate-500">
                      Total Outstanding: <strong className="text-slate-900 font-mono">{formatINR(totalOutstanding)}</strong>
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100/75 text-slate-600 font-bold border-b border-slate-200 text-[11px]">
                        <tr>
                          <th className="py-2.5 px-3">Loan / Acc No.</th>
                          <th className="py-2.5 px-3">Facility / Product</th>
                          <th className="py-2.5 px-3 text-right">Sanction Amount</th>
                          <th className="py-2.5 px-3 text-right">Loan Balance</th>
                          <th className="py-2.5 px-3 text-center">NPA Date</th>
                          <th className="py-2.5 px-3 text-right">Overdue Amount</th>
                          <th className="py-2.5 px-3 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-sans">
                        {relatedAccounts.map((acc, idx) => (
                          <tr
                            key={acc.accountId}
                            className={`hover:bg-blue-50/50 transition ${
                              acc.accountId === account.accountId ? 'bg-blue-50/30 font-medium' : ''
                            }`}
                          >
                            <td className="py-2.5 px-3">
                              <div className="font-mono font-bold text-slate-900 flex items-center gap-1.5">
                                <span>{acc.loanNumber}</span>
                                {acc.accountId === account.accountId && (
                                  <span className="text-[9px] bg-blue-100 text-blue-800 px-1.5 py-0.2 rounded font-bold">
                                    Current
                                  </span>
                                )}
                              </div>
                              <span className="text-[10px] text-slate-500">{acc.accountId}</span>
                            </td>
                            <td className="py-2.5 px-3">
                              <span className="font-semibold text-slate-800">{acc.facility || acc.loanType}</span>
                              <span className="text-[10px] text-slate-500 block">{acc.branch}</span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                              {formatINR(acc.sanctionAmount)}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-red-600">
                              {formatINR(acc.outstandingAmount)}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-800 font-mono text-[11px] border border-amber-200">
                                {displayDate(acc.npaDate) || '\u2014'}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-amber-600">
                              {formatINR(acc.overdueAmount)}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                acc.accountStatus === 'Recovered' ? 'bg-emerald-100 text-emerald-800' :
                                acc.accountStatus === 'PTP Pending' ? 'bg-amber-100 text-amber-800' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {acc.accountStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Date-wise Collection Statement.
                    An agent standing in front of the customer gets asked "how
                    much have I already paid you?" and had nowhere in this
                    window to answer it - the totals above give one figure but
                    not the receipts behind it. This lists every receipt so the
                    answer can be read out, newest first because the argument is
                    almost always about the last payment. */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-emerald-600" />
                      Collection Statement ({accountRecoveries.length}{' '}
                      {accountRecoveries.length === 1 ? 'Receipt' : 'Receipts'})
                    </h4>
                    <span className="text-[11px] text-slate-500">
                      Total Collected:{' '}
                      <strong className="text-emerald-700 font-mono">
                        {formatINR(accountRecoveriesTotal)}
                      </strong>
                    </span>
                  </div>

                  {accountRecoveries.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-500">
                      No collections recorded yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-100/75 text-slate-600 font-bold border-b border-slate-200 text-[11px]">
                          <tr>
                            <th className="py-2.5 px-3">Date</th>
                            <th className="py-2.5 px-3">Receipt No.</th>
                            <th className="py-2.5 px-3 text-right">Amount</th>
                            <th className="py-2.5 px-3">Payment Mode</th>
                            <th className="py-2.5 px-3">Collected By</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 font-sans">
                          {accountRecoveries.map((rec, idx) => (
                            <tr
                              key={rec.id || rec.recoveryId || `recovery-${idx}`}
                              className="hover:bg-emerald-50/40 transition"
                            >
                              <td className="py-2.5 px-3 font-mono text-slate-700">
                                {rec.recoveryDate || 'N/A'}
                              </td>
                              <td className="py-2.5 px-3 font-mono font-bold text-slate-900">
                                {rec.receiptNumber || rec.recoveryId || 'N/A'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-700">
                                {formatINR(rec.amount || 0)}
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold text-[10px] border border-slate-200">
                                  {rec.paymentMode || 'N/A'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-semibold text-slate-800">
                                  {rec.agentName || 'N/A'}
                                </span>
                                {rec.agentId && (
                                  <span className="text-[10px] text-slate-500 block font-mono">
                                    {rec.agentId}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200 font-bold text-[11px]">
                          <tr>
                            <td className="py-2.5 px-3 text-slate-700" colSpan={2}>
                              Total Collected
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-emerald-700">
                              {formatINR(accountRecoveriesTotal)}
                            </td>
                            <td className="py-2.5 px-3" colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Latest Agent Note + previous notes.
                    Older notes are never overwritten -- every note is kept in
                    the account's history. The three most recent previous
                    notes are shown here; the rest are in the Notes tab. */}
                {agentNotes.length === 0 && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500">
                    <span className="font-bold text-slate-600">AGENT NOTE</span> — none yet for
                    this customer. Anything the app recorded itself (collections, OTS, rating
                    changes) is under Notes.
                  </div>
                )}

                {agentNotes.length > 0 && (
                  <div className="bg-amber-50/70 p-4 rounded-xl border border-amber-200 space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-amber-900 flex items-center gap-1.5">
                          <MessageSquare className="w-4 h-4 text-amber-700" />
                          AGENT NOTE ({agentNotes[0].date} {agentNotes[0].time})
                        </span>
                        <span className="text-[11px] text-amber-700 font-medium">
                          By {agentNotes[0].agentName}
                        </span>
                      </div>
                      <p className="text-sm text-slate-900 leading-relaxed font-semibold mt-1.5">
                        "{agentNotes[0].note}"
                      </p>
                    </div>

                    {agentNotes.length > 1 && (
                      <div className="pt-3 border-t border-amber-200 space-y-2">
                        <p className="text-[11px] font-bold text-amber-900 uppercase tracking-wide">
                          Previous Notes
                        </p>

                        {agentNotes.slice(1, 4).map((n, i) => (
                          <div
                            key={n.id || `prev-note-${i}`}
                            className="bg-white/70 rounded-lg border border-amber-200 px-3 py-2"
                          >
                            <div className="flex items-center justify-between text-[10px] text-amber-800 font-semibold">
                              <span>{n.date} {n.time}</span>
                              <span>{n.agentName}</span>
                            </div>
                            <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                              "{n.note}"
                            </p>
                          </div>
                        ))}

                        {agentNotes.length > 4 && (
                          <button
                            type="button"
                            onClick={() => setActiveTab('notes')}
                            className="w-full text-[11px] font-bold text-amber-900 hover:text-amber-950 underline underline-offset-2 pt-0.5"
                          >
                            View all {agentNotes.length} agent notes
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB: UPLOADED MASTER RECORD & BANK EXCEL COLUMNS
                Role is checked on the panel too, not only on the tab button, so
                an agent left on this tab by an earlier session cannot still see
                the sheet. */}
            {activeTab === 'uploaded_data' && currentUser.role !== 'agent' && (
              <div className="space-y-4">
                {/* Header with Search and Stats */}
                <div className="bg-slate-900 text-white p-4 rounded-xl flex flex-wrap items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-xl">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white flex items-center gap-2">
                        Uploaded Bank Master Record
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono">
                          100% Data Preserved
                        </span>
                      </h4>
                      <p className="text-xs text-slate-400">
                        Complete raw &amp; structured parameters imported from Bank / NBFC Excel file.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search uploaded fields..."
                        value={uploadedFieldFilter}
                        onChange={(e) => setUploadedFieldFilter(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 text-white rounded-lg text-xs placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const payload = {
                          ...account,
                          customFields: account.customFields || {},
                        };
                        navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                        alert('Uploaded customer record copied to clipboard as JSON!');
                      }}
                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition cursor-pointer"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      <span>Copy JSON</span>
                    </button>
                  </div>
                </div>

                {/* 14 Standard Master Columns Grid */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      Standard Bank Master Fields (14 Core Columns)
                    </h5>
                    <span className="text-[11px] text-slate-500 font-mono">
                      CIF: {account.customerCode || account.accountId}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 text-xs">
                    {[
                      { key: 'BRANCH_COD', label: 'Branch Code', val: account.branchCode || '\u2014' },
                      { key: 'BRANCH_NAME', label: 'Branch Name', val: account.branch || '\u2014' },
                      { key: 'ACCT_NO', label: 'Account / Loan No.', val: account.loanNumber, highlight: true },
                      { key: 'NAME', label: 'Customer Full Name', val: account.customerName, highlight: true },
                      /* PROD_DESC is the bank's own wording for the product --
                         "TWO WHEELER LOAN DIRECT", "SB-OD AGAINST FD". It is
                         kept verbatim on the account as productDescription.
                         This row used to show `loanType` instead, which is the
                         normalised category the importer derives for filtering
                         and reporting. So the field labelled PROD_DESC showed
                         something other than what the sheet said, on the one
                         screen that exists to show the master record as the
                         bank supplied it. loanType stays only as the fallback
                         for accounts imported before the verbatim value was
                         being kept. */
                      {
                        key: 'PROD_DESC',
                        label: 'Product (PROD_DESC)',
                        val: productOf(account),
                      },
                      { key: 'FACILITY', label: 'Facility Type', val: account.facility || account.loanType },
                      { key: 'LIMIT_SANCTI', label: 'Sanction Limit', val: formatINR(account.sanctionAmount), mono: true },
                      { key: 'LOAN_B', label: 'Loan Outstanding Balance', val: formatINR(account.outstandingAmount), mono: true, color: 'text-red-600 font-bold' },
                      { key: 'NPA_DT', label: 'NPA Date', val: displayDate(account.npaDate) || '\u2014', mono: true, color: 'text-amber-700 font-bold' },
                      { key: 'CONTACT NO.', label: 'Primary Contact Mobile', val: account.mobile, mono: true },
                      { key: 'CUSTOMER_BALANCE', label: 'Customer Balance (Total Overdue)', val: formatINR(account.customerBalance || account.overdueAmount || account.outstandingAmount), mono: true, color: 'text-amber-600 font-bold' },
                      { key: 'CITY', label: 'Customer City', val: account.city || '\u2014' },
                      { key: 'AREA', label: 'Territory Area', val: account.area || '\u2014' },
                      { key: 'ZONE', label: 'Administrative Zone', val: account.zone || '\u2014' },
                      { key: 'ADDRESS', label: 'Registered Address', val: account.address },
                      { key: 'AGENT_ID', label: 'Allocated Agent ID', val: account.assignedAgentId || 'Unassigned', mono: true },
                      { key: 'AGENT_NAME', label: 'Allocated Agent Name', val: account.assignedAgentName || 'Unallocated' },
                      { key: 'STATUS', label: 'Account Recovery Status', val: account.accountStatus },
                      { key: 'RISK_CATEGORY', label: 'Risk Category', val: account.customerCategory || 'NPA Standard' },
                      { key: 'ACCOUNT_CATEGORY', label: 'Account Category (Excel)', val: account.accountCategory || '—', mono: true },
                    ]
                      .filter(
                        (item) =>
                          !uploadedFieldFilter ||
                          item.key.toLowerCase().includes(uploadedFieldFilter.toLowerCase()) ||
                          item.label.toLowerCase().includes(uploadedFieldFilter.toLowerCase()) ||
                          String(item.val).toLowerCase().includes(uploadedFieldFilter.toLowerCase())
                      )
                      .map((item) => (
                        <div key={item.key} className="p-3 bg-white space-y-1">
                          <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
                            <span>{item.key}</span>
                            <span className="text-[9px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{item.label}</span>
                          </div>
                          <p className={`text-xs ${item.color || 'text-slate-900'} ${item.mono ? 'font-mono' : ''} ${item.highlight ? 'font-bold' : 'font-medium'} break-words`}>
                            {item.val || '—'}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Additional / Custom Fields from Excel */}
                {account.customFields && Object.keys(account.customFields).length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                    <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
                      <h5 className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-2">
                        <Layers className="w-4 h-4 text-indigo-600" />
                        Custom &amp; Extended Spreadsheet Columns ({Object.keys(account.customFields).length} Columns)
                      </h5>
                      <span className="text-[11px] text-indigo-700 font-medium">Auto-mapped from Uploaded File</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 text-xs">
                      {Object.entries(account.customFields)
                        .filter(
                          ([k, v]) =>
                            !uploadedFieldFilter ||
                            k.toLowerCase().includes(uploadedFieldFilter.toLowerCase()) ||
                            String(v).toLowerCase().includes(uploadedFieldFilter.toLowerCase())
                        )
                        .map(([key, value]) => (
                          <div key={key} className="p-3 bg-white space-y-1">
                            <span className="text-[10px] font-mono text-indigo-600 font-bold block">{key}</span>
                            <p className="text-xs text-slate-800 font-medium break-words font-mono">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value || '—')}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Multi-Account Association Intelligence */}
                {isMultiple && (
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 space-y-3">
                    <h5 className="text-xs font-bold text-purple-900 uppercase tracking-wider flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-600" />
                      Consolidated Multi-Account Cross-Reference
                    </h5>
                    <p className="text-xs text-purple-800 leading-relaxed">
                      Our system grouped <strong>{relatedAccounts.length} loans</strong> for this customer because they share identical 
                      outstanding balance (₹{account.outstandingAmount.toLocaleString('en-IN')}), NPA Date ({displayDate(account.npaDate) || 'Recorded'}), and borrower identity ({account.customerName}).
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      {relatedAccounts.map((r, i) => (
                        <div key={r.accountId} className="p-2.5 bg-white rounded-lg border border-purple-200 flex items-center justify-between">
                          <div>
                            <span className="text-[10px] text-purple-600 font-bold block">Loan #{i + 1}</span>
                            <span className="font-mono font-bold text-slate-800">{r.loanNumber}</span>
                            <span className="text-[11px] text-slate-500 block">{r.facility || r.loanType}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-mono font-bold text-red-600 block">{formatINR(r.outstandingAmount)}</span>
                            <span className="text-[10px] text-slate-500">{r.branch}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB: DOCUMENTS & MEDIA (KYC, Geotagged Photos, Voice Notes, Cloud Storage) */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    Customer Documents &amp; Field Media
                  </h4>
                  <button
                    onClick={() => setShowDocUpload(true)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg flex items-center gap-1.5 transition cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Attach Document</span>
                  </button>
                </div>

                {/* Uploaded Documents List */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>KYC &amp; Legal Notices ({accountDocs.length})</span>
                    <span className="text-[10px] text-slate-500">Synced to Cloud Firestore / Storage</span>
                  </div>

                  {accountDocs.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">
                      No KYC documents uploaded yet. Click "Attach Document" to add Aadhaar, PAN, or Demand Notices.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 text-xs">
                      {accountDocs.map((doc, idx) => (
                        <div key={doc.id || `doc-${idx}`} className="p-3 flex items-center justify-between hover:bg-slate-50 transition">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div>
                              <h6 className="font-bold text-slate-900">{doc.name}</h6>
                              <p className="text-[11px] text-slate-500">
                                {doc.category} • Uploaded {doc.uploadedAt} by {doc.uploadedBy}
                              </p>
                            </div>
                          </div>
                          <a
                            href={doc.fileUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg flex items-center gap-1 text-[11px] transition"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>View</span>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Geotagged Field Photos */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5 text-blue-600" />
                      Geotagged Watermarked Photos ({accountPhotos.length})
                    </span>
                    <span className="text-[10px] text-slate-500">GPS Timestamp &amp; Agent Tagged</span>
                  </div>

                  {accountPhotos.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">
                      No field visit photos captured yet for this borrower. Photos taken during mobile app visits appear here.
                    </div>
                  ) : (
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      {accountPhotos.map((photo, idx) => (
                        <div key={photo.id || `photo-${idx}`} className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                          {/* These used to read photo.url / photo.caption /
                              photo.location, none of which exist on a photo
                              record -- so every thumbnail here was broken and
                              the GPS line always fell back to "GPS Verified". */}
                          <img
                            src={photo.downloadUrl || photo.dataUrl}
                            alt="Field visit evidence"
                            title="Click to open the full-size photo"
                            onClick={() => {
                              const url = photo.downloadUrl || photo.dataUrl;
                              if (url) window.open(url, '_blank', 'noopener');
                            }}
                            className="w-full h-32 object-cover rounded-lg border border-slate-200 cursor-zoom-in hover:opacity-90 transition"
                            referrerPolicy="no-referrer"
                          />
                          <div className="text-[11px] text-slate-600 space-y-0.5">
                            <div className="flex items-center justify-between font-bold text-slate-800">
                              <span>{photo.accountId || 'Field Visit Photo'}</span>
                              <span className="font-mono text-[10px] text-slate-500">
                                {photo.date} {photo.time}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1 font-mono">
                              <MapPin className="w-3 h-3 text-red-500 shrink-0" />
                              <span>
                                {typeof photo.latitude === 'number' && typeof photo.longitude === 'number'
                                  ? `${photo.latitude.toFixed(4)}, ${photo.longitude.toFixed(4)}`
                                  : 'GPS Verified'}
                              </span>
                            </div>
                            <p
                              className={`italic ${
                                photo.agentRemark ? 'text-slate-600' : 'text-slate-400'
                              }`}
                            >
                              {photo.agentRemark
                                ? `"${photo.agentRemark}"`
                                : 'No note added yet.'}
                            </p>
                            {photo.noteEdited && (
                              <p className="text-[10px] text-amber-700 font-semibold">
                                Note filled in after the visit.
                              </p>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditPhotoNoteInWindow(photo)}
                              disabled={editingNoteId === (photo.id || photo.photoId)}
                              className="flex-1 px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 font-bold text-[10px] hover:bg-blue-100 disabled:opacity-50 transition"
                            >
                              {editingNoteId === (photo.id || photo.photoId)
                                ? 'Saving…'
                                : photo.agentRemark
                                ? 'Edit Note'
                                : 'Add Note'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePhotoInWindow(photo)}
                              disabled={deletingPhotoId === (photo.id || photo.photoId)}
                              className="flex-1 px-2.5 py-1.5 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 font-bold text-[10px] hover:bg-rose-100 disabled:opacity-50 transition"
                            >
                              {deletingPhotoId === (photo.id || photo.photoId)
                                ? 'Deleting…'
                                : 'Delete Photo'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Voice Notes */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Mic className="w-3.5 h-3.5 text-red-600" />
                      Audio &amp; Voice Interaction Recordings ({accountVoices.length})
                    </span>
                    <span className="text-[10px] text-slate-500">Call &amp; Visit Audio</span>
                  </div>

                  {accountVoices.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">
                      No voice recordings logged yet. Field agents can record audio notes on the Android app.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 text-xs">
                      {accountVoices.map((voice, idx) => (
                        <div key={voice.id || `voice-${idx}`} className="p-3 flex items-center justify-between">
                          <div>
                            <h6 className="font-bold text-slate-800">{voice.title || 'Voice Note'}</h6>
                            <p className="text-[11px] text-slate-500">
                              Recorded at {voice.timestamp} • Duration: {voice.duration || '0:45'}s
                            </p>
                          </div>
                          {voice.audioUrl && voice.audioUrl !== 'mock_audio_url' ? (
                            <audio controls preload="none" className="h-8 max-w-[200px]" src={voice.audioUrl}>
                              Your browser does not support audio playback.
                            </audio>
                          ) : (
                            <span className="text-[10px] font-mono text-pink-700 bg-pink-50 px-2.5 py-1 rounded border border-pink-200">
                              Cloud Audio Recording
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 2: ALL ACCOUNTS FULL DETAIL CARDS */}
            {activeTab === 'accounts' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    {relatedAccounts.length} Linked Accounts for {account.customerName}
                  </h4>
                  <span className="text-xs text-slate-500">
                    Grouped by Balance + NPA Date Matching
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedAccounts.map((acc, index) => (
                    <div
                      key={acc.accountId || `acc-card-${index}`}
                      className={`p-4 rounded-xl border transition shadow-xs ${
                        acc.accountId === account.accountId
                          ? 'bg-blue-50/40 border-blue-300 ring-1 ring-blue-300'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-slate-800 text-white font-bold text-[10px] flex items-center justify-center">
                              {index + 1}
                            </span>
                            <h5 className="font-bold text-sm text-slate-900 font-mono">
                              {acc.loanNumber}
                            </h5>
                          </div>
                          <span className="text-xs text-slate-500 font-medium block ml-7">
                            {acc.facility || acc.loanType} • {acc.branch}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          acc.accountStatus === 'Recovered' ? 'bg-emerald-100 text-emerald-800' :
                          acc.accountStatus === 'PTP Pending' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {acc.accountStatus}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded-lg border border-slate-200/80 mb-3">
                        <div>
                          <span className="text-[10px] text-slate-500 block">Outstanding Balance</span>
                          <span className="font-bold text-red-600 font-mono text-sm">
                            {formatINR(acc.outstandingAmount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">Overdue Amount</span>
                          <span className="font-bold text-amber-600 font-mono text-sm">
                            {formatINR(acc.overdueAmount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">NPA Date</span>
                          <span className="font-semibold text-slate-800 font-mono text-xs">
                            {displayDate(acc.npaDate) || '\u2014'}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">Monthly EMI</span>
                          <span className="font-semibold text-slate-800 font-mono text-xs">
                            {formatINR(acc.emi)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="text-[11px] text-slate-500">
                          Sanction Limit: <strong className="text-slate-800">{formatINR(acc.sanctionAmount)}</strong>
                        </span>
                        {onSelectLinkedAccount && acc.accountId !== account.accountId && (
                          <button
                            onClick={() => onSelectLinkedAccount(acc.accountId)}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
                          >
                            <span>Inspect This Loan</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 3: AGENT NOTES HISTORY & INLINE COMPOSER */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                {/* Add Note Composer Box */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    Record Agent Note for Customer ({account.customerName})
                  </h4>

                  <form onSubmit={handleAddInlineNote} className="space-y-3 text-xs">
                    <div className="flex flex-wrap gap-2">
                      {['General', 'PTP Commitment', 'Field Visit', 'Customer Dispute', 'Payment Promise', 'Skipped'].map((cat) => (
                        <button
                          key={`note-cat-${cat}`}
                          type="button"
                          onClick={() => setNewNoteCategory(cat)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition cursor-pointer ${
                            newNoteCategory === cat
                              ? 'bg-blue-600 text-white font-bold'
                              : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>

                    <textarea
                      rows={3}
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Add specific note, ground remarks, dispute details, customer commitments, or visit findings..."
                      className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400"
                    />

                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-500">
                        Author: <strong>{currentUser.name}</strong> ({currentUser.agentId || currentUser.role})
                        {' · '}
                        <strong
                          className={
                            agentNotes.length >= MAX_USER_NOTES_PER_ACCOUNT
                              ? 'text-amber-700'
                              : 'text-slate-600'
                          }
                        >
                          {agentNotes.length} of {MAX_USER_NOTES_PER_ACCOUNT} notes used
                        </strong>
                      </span>
                      <button
                        type="submit"
                        disabled={
                          !newNoteText.trim() || agentNotes.length >= MAX_USER_NOTES_PER_ACCOUNT
                        }
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow-xs transition cursor-pointer disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>Save Agent Note</span>
                      </button>
                    </div>

                    {noteFeedback && (
                      <p className="text-[11px] font-bold text-slate-700 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
                        {noteFeedback}
                      </p>
                    )}

                    {agentNotes.length >= MAX_USER_NOTES_PER_ACCOUNT && (
                      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                        This customer has all {MAX_USER_NOTES_PER_ACCOUNT} notes. Delete one below
                        to free a place — the count only includes notes people wrote, never the
                        system's own activity entries.
                      </p>
                    )}
                  </form>
                </div>

                {/* Chronological Notes Feed */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Agent Notes History ({agentNotes.length} of {MAX_USER_NOTES_PER_ACCOUNT})
                    </h4>
                  </div>

                  {agentNotes.length === 0 ? (
                    <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-xs font-medium">No agent notes recorded yet.</p>
                      <p className="text-slate-400 text-[11px] mt-0.5">Use the box above to write the first note for this customer.</p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {agentNotes.map((note, idx) => (
                        <div
                          key={note.id || `note-${idx}`}
                          className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs space-y-2"
                        >
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{note.agentName}</span>
                              <span className="text-[10px] font-mono text-slate-500">({note.agentId})</span>
                              <span className="px-2 py-0.2 rounded-md bg-blue-50 text-blue-700 font-semibold text-[10px] border border-blue-200">
                                {note.category || 'General'}
                              </span>
                              {note.accountLoanNumber && (
                                <span className="text-[10px] text-slate-400 font-mono">
                                  • Loan: {note.accountLoanNumber}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-[11px] text-slate-400 font-mono">
                                {note.date} {note.time}
                              </span>
                              {canDeleteNote(note) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteNote(note.id, note.note)}
                                  title={
                                    currentUser.role === 'admin'
                                      ? 'Delete this note'
                                      : 'Delete your note'
                                  }
                                  className="p-1 rounded-lg text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-slate-700 font-medium leading-relaxed whitespace-pre-wrap pl-1 border-l-2 border-blue-500">
                            {note.note}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                {/* The app's own entries -- allocation, reallocation, Excel
                    refresh, collections, OTS steps -- are no longer listed
                    here. This tab is what an agent wrote about the customer,
                    and a dozen machine lines between two real notes buried
                    them. Nothing is lost: those entries are still recorded
                    against the account and are read in the account timeline,
                    which is where an audit belongs. */}
                </div>
              </div>
            )}

            {/* TAB 4: RECOVERY ACTIONS */}
            {activeTab === 'actions' && (
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Field &amp; Office Recovery Actions for {account.customerName}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <button
                    onClick={() => setShowFollowUp(true)}
                    className="p-4 bg-amber-50/60 hover:bg-amber-100/80 border border-amber-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-amber-500 text-white rounded-lg shrink-0">
                      <PhoneCall className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Record Follow-up Call</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Log discussion status, customer commitment &amp; next follow-up date.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowPTP(true)}
                    className="p-4 bg-purple-50/60 hover:bg-purple-100/80 border border-purple-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-purple-600 text-white rounded-lg shrink-0">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Create PTP Commitment</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Set promised payment date, amount, mode &amp; settlement terms.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowRecovery(true)}
                    className="p-4 bg-emerald-50/60 hover:bg-emerald-100/80 border border-emerald-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-emerald-600 text-white rounded-lg shrink-0">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Record Payment Recovery</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Issue e-Receipt, credit 10% agent commission, and sync to Drive.</p>
                    </div>
                  </button>

                  <button
                    onClick={handleInitiateVisit}
                    className="p-4 bg-blue-50/60 hover:bg-blue-100/80 border border-blue-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-blue-600 text-white rounded-lg shrink-0">
                      <Navigation className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Start Geo-Tagged Field Visit</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Launch live GPS route, capture watermarked photo &amp; voice notes.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowAddNoteModal(true)}
                    className="p-4 bg-indigo-50/60 hover:bg-indigo-100/80 border border-indigo-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-indigo-600 text-white rounded-lg shrink-0">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Add Detailed Agent Note</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Tag customer behavior, property status, or skip tracing intelligence.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowDocUpload(true)}
                    className="p-4 bg-teal-50/60 hover:bg-teal-100/80 border border-teal-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-teal-600 text-white rounded-lg shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">Attach Document / KYC File</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Upload borrower KYC, notices, or receipts to secure Cloud Storage.</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowAI(true)}
                    className="p-4 bg-rose-50/60 hover:bg-rose-100/80 border border-rose-200 rounded-xl text-left transition flex items-start gap-3 cursor-pointer"
                  >
                    <div className="p-2.5 bg-rose-600 text-white rounded-lg shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-bold text-xs text-slate-900">AI Risk &amp; Recovery Strategy</h5>
                      <p className="text-[11px] text-slate-600 mt-0.5">Get Gemini ML default prediction, negotiation tips &amp; WhatsApp script.</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

          </div>
          </div>

          {/* Bottom Action Footer.
              Phones: one grid of 4 equal buttons (two neat rows, same height).
              Desktop keeps the original wrapping layout. */}
          <div className="px-2 sm:px-5 py-2 sm:py-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] bg-slate-50 border-t border-slate-200 grid grid-cols-4 gap-1.5 sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-2 text-xs shrink-0">
            <div className="contents sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:min-w-0">
              <a
                href={`tel:${account.mobile}`}
                className="h-14 sm:h-auto px-1 sm:px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 text-center leading-tight text-[11px] sm:text-xs shadow-xs transition"
              >
                <PhoneCall className="w-3.5 h-3.5" />
                <span>Call Borrower</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  setWhatsAppCategory('PAYMENT_REMINDER');
                  setShowWhatsAppDraft(true);
                }}
                className="h-14 sm:h-auto px-1 sm:px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 text-center leading-tight text-[11px] sm:text-xs shadow-xs transition cursor-pointer"
                title="Open WhatsApp Notification Studio (Auto-Generated & Manual Templates)"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>WhatsApp Notice</span>
              </button>
              <button
                onClick={() => setShowAddNoteModal(true)}
                className="h-14 sm:h-auto px-1 sm:px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold rounded-xl flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 text-center leading-tight text-[11px] sm:text-xs transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Note</span>
              </button>
            </div>

            <div className="contents sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:min-w-0">
              {/* OTS SCHEME STANDS APART FROM THE REST OF THIS BAR.
                  It is solid indigo with a ring, so the settlement control is
                  visibly a different kind of action from the logging ones.
                  The divider is desktop-only. */}
              <span className="hidden sm:block w-px h-6 bg-slate-300" aria-hidden="true" />
              <button
                id="footer-open-ots-btn"
                onClick={() => setShowOTSScheme(true)}
                title="Work out or change the one-time settlement for this customer"
                className="h-14 sm:h-auto px-1 sm:px-4 py-1.5 sm:py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-black rounded-xl shadow-sm ring-2 ring-indigo-300 transition flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 text-center leading-tight text-[11px] sm:text-xs cursor-pointer"
              >
                <Calculator className="w-4 h-4" />
                <span className="tracking-wide">OTS SCHEME</span>
              </button>
              <button
                id="footer-open-whatsapp-ots-btn"
                onClick={() => {
                  setShowWhatsAppOTSOffer(true);
                }}
                className="h-14 sm:h-auto px-1 sm:px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-xs transition flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 text-center leading-tight text-[11px] sm:text-xs cursor-pointer"
                title="Send official Marathi OTS offer on WhatsApp"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>WhatsApp OTS</span>
              </button>
              <button
                onClick={() => setShowPTP(true)}
                className="h-14 sm:h-auto px-1 sm:px-3.5 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-900 font-bold rounded-xl transition flex items-center justify-center text-center leading-tight text-[11px] sm:text-xs cursor-pointer"
              >
                Create PTP
              </button>
              <button
                onClick={() => setShowRecovery(true)}
                className="h-14 sm:h-auto px-1 sm:px-3.5 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-900 font-bold rounded-xl transition flex items-center justify-center text-center leading-tight text-[11px] sm:text-xs cursor-pointer"
              >
                Collect Pay
              </button>
              <button
                onClick={onClose}
                className="h-14 sm:h-auto px-1 sm:px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition flex items-center justify-center text-center leading-tight text-[11px] sm:text-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Sub-modals */}
      <OTSSchemeModal
        account={account}
        isOpen={showOTSScheme}
        onClose={() => setShowOTSScheme(false)}
        onOpenWhatsAppOffer={() => {
          setShowOTSScheme(false);
          setShowWhatsAppOTSOffer(true);
        }}
      />
      <WhatsAppOTSOfferModal
        account={account}
        relatedAccounts={relatedAccounts}
        isOpen={showWhatsAppOTSOffer}
        onClose={() => setShowWhatsAppOTSOffer(false)}
      />
      {account && (
        <WhatsAppDraftModal
          account={account}
          isOpen={showWhatsAppDraft}
          onClose={() => setShowWhatsAppDraft(false)}
          defaultCategory={whatsAppCategory}
        />
      )}
      <LogFollowUpModal
        account={account}
        isOpen={showFollowUp}
        onClose={() => setShowFollowUp(false)}
      />
      <CreatePTPModal
        account={account}
        isOpen={showPTP}
        onClose={() => setShowPTP(false)}
      />
      <RecordRecoveryModal
        account={account}
        relatedAccounts={relatedAccounts}
        isOpen={showRecovery}
        onClose={() => setShowRecovery(false)}
      />
      <UploadDocumentModal
        account={account}
        isOpen={showDocUpload}
        onClose={() => setShowDocUpload(false)}
      />
      <AIRecoveryDrawer
        account={account}
        isOpen={showAI}
        onClose={() => setShowAI(false)}
      />
      <AddAgentNoteModal
        account={account}
        isOpen={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
      />
      <CloseAccountModal
        account={account}
        isOpen={showCloseAccountModal}
        onClose={() => setShowCloseAccountModal(false)}
        onSuccess={() => {
          // The close-account window has already shut itself by now, so this
          // only has to close the customer window.
          setShowCloseAccountModal(false);
          onClose();
        }}
      />
    </>
  );
};
